import { Extension, findParentNodeClosestToPos, isNodeEmpty } from "@tiptap/core";
import { type Node } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import type { Alignment } from "@/components/doc/right-sidebar/align-dropdown/AlignDropdown";

export type PlaceholderOptions = {
  types: string[];
  excludeFromParentTypes: string[];
  placeholder?: ((node: Node, docNode: Node) => string | undefined) | string;
};

type PluginState = {
  decorations: DecorationSet | null;
};

export const Placeholder = Extension.create<PlaceholderOptions>({
  name: "placeholder",

  addOptions() {
    return {
      types: ["paragraph", "heading"],
      excludeFromParentTypes: [
        "tableHeader",
        "tableCell",
        "blockquote",
        "callout",
        "listItem",
        "taskItem",
      ],
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<PluginState>({
        key: new PluginKey("placeholder"),
        state: {
          init: () => ({
            decorations: null,
          }),
          apply: (tr, state) => {
            const { placeholder } = this.options;

            if (!placeholder) {
              return { decorations: null };
            }

            if (!tr.docChanged && !tr.selectionSet) {
              return state;
            }

            const { selection } = tr;
            const nodeWithPos = findParentNodeClosestToPos(selection.$head, (n) =>
              this.options.types.includes(n.type.name),
            );

            if (!nodeWithPos) {
              return { decorations: null };
            }

            const { parent } = tr.doc.resolve(nodeWithPos.pos);

            if (this.options.excludeFromParentTypes.includes(parent.type.name)) {
              return { decorations: null };
            }

            const { node, pos } = nodeWithPos;
            const placeholderText =
              typeof placeholder === "function" ? placeholder(node, tr.doc) : placeholder;

            if (!placeholderText) {
              return { decorations: null };
            }

            const hasAnchor = selection.anchor >= pos && selection.anchor <= pos + node.nodeSize;
            const isEmpty = !node.isLeaf && isNodeEmpty(node);

            if (hasAnchor && isEmpty) {
              const decoration = Decoration.node(
                pos,
                pos + node.nodeSize,
                placeholderAttrs(node, placeholderText),
              );

              return {
                decorations: DecorationSet.create(tr.doc, [decoration]),
              };
            }

            return { decorations: null };
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)?.decorations ?? null;
          },
        },
      }),
    ];
  },
});

/**
 * The placeholder is drawn by a `::before` in CSS (see `.tiptap-line-placeholder` in theme.css), never
 * as DOM inside the line: a non-editable element there makes ProseMirror add a separator `<img>`, and
 * Android Chrome's long-press caret lands on the other side of it. ProseMirror then rewrites the
 * caret, which dismisses the native Paste menu.
 */
function placeholderAttrs(node: Node, placeholder: string) {
  const alignment = (node.attrs.alignment as Alignment | null) ?? "left";

  return {
    class: "tiptap-line-placeholder",
    "data-placeholder": placeholder,
    "data-placeholder-align": alignment,
    "data-placeholder-type": node.type.name,
    style: `--tiptap-placeholder: ${JSON.stringify(placeholder)}`,
  };
}
